/**
 * THE ONLY FILE IN THIS APP THAT READS THE ENVIRONMENT (BUILD.md §5).
 *
 * Everything else takes its configuration from `adminEnv()`. Vite inlines `import.meta.env` at
 * build time, so every value here is public by construction: a secret must never appear in this
 * file, and none of the names below is one.
 */

import { TENERGY_WALLETCONNECT_PROJECT_ID } from "@tenergy/web-core";

interface RawEnv {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_NILE_API_BASE_URL?: string;
  readonly VITE_TRON_NETWORK?: string;
  readonly VITE_TRON_FULL_HOST?: string;
  readonly VITE_WALLETCONNECT_PROJECT_ID?: string;
  readonly VITE_ENABLE_MSW?: string;
  readonly VITE_API_METRICS_URL?: string;
  readonly VITE_WORKER_METRICS_URL?: string;
}

export interface AdminEnv {
  apiBaseUrl: string;
  /**
   * The Nile API, with the Nile tab's own admin session (docs/product/admin-auth.md §10). On Pages it is `/nile/v1` on
   * this host (functions/nile/v1 proxies it), so the CSP's `connect-src` stays this origin; the dev server calls the
   * Nile API directly.
   */
  nileApiBaseUrl: string;
  tronNetwork: "mainnet" | "nile";
  tronFullHost: string | null;
  walletConnectProjectId: string | null;
  /** The mocked API, for the dev server, the preview build and the screenshots. Never in prod. */
  mockApi: boolean;
  /**
   * Where the two Prometheus endpoints are reachable FROM THE BROWSER. Same-origin paths by
   * default: `/metrics` carries no CORS headers, so the dev server and the production reverse
   * proxy forward them (`vite.config.ts`, `README.md`).
   */
  apiMetricsUrl: string;
  workerMetricsUrl: string;
}

function text(value: string | undefined, fallback: string): string {
  return value === undefined || value.trim() === "" ? fallback : value.trim();
}

function optional(value: string | undefined): string | null {
  return value === undefined || value.trim() === "" ? null : value.trim();
}

/**
 * A path (`/v1`, or `same-origin`) is the admin's own origin: on Cloudflare Pages the function under `functions/v1` proxies it to the
 * api (owner 2026-10-07), so one build serves its preview URL and admin.tenergy.me alike. The client builds URLs
 * with `new URL()`, so the base is made absolute here.
 */
function absolute(base: string): string {
  // `same-origin` is how the deploy script says `/v1`: Git Bash on Windows rewrites a value starting with `/` into a
  // Windows path on its way to bun (it shipped `C:/Program Files/Git/v1`, 2026-10-07).
  if (base === "same-origin") base = "/v1";
  if (!base.startsWith("/")) return base;
  const origin = typeof location === "undefined" ? "http://localhost" : location.origin;
  return `${origin}${base}`;
}

let cached: AdminEnv | null = null;

export function adminEnv(): AdminEnv {
  if (cached !== null) return cached;
  const raw = (import.meta.env ?? {}) as RawEnv;
  const network = text(raw.VITE_TRON_NETWORK, "nile");
  const mockApi = text(raw.VITE_ENABLE_MSW, "0") === "1";
  const apiBaseUrl = absolute(text(raw.VITE_API_BASE_URL, import.meta.env.DEV ? "http://localhost:3001/v1" : "/v1"));
  cached = {
    apiBaseUrl,
    // The mocked API answers both networks on one base.
    nileApiBaseUrl: mockApi
      ? apiBaseUrl
      : absolute(text(raw.VITE_NILE_API_BASE_URL, import.meta.env.DEV ? "https://api-nile.tenergy.me/v1" : "/nile/v1")),
    tronNetwork: network === "mainnet" ? "mainnet" : "nile",
    tronFullHost: optional(raw.VITE_TRON_FULL_HOST),
    walletConnectProjectId: optional(raw.VITE_WALLETCONNECT_PROJECT_ID) ?? TENERGY_WALLETCONNECT_PROJECT_ID,
    mockApi,
    apiMetricsUrl: text(raw.VITE_API_METRICS_URL, "/metrics/api"),
    workerMetricsUrl: text(raw.VITE_WORKER_METRICS_URL, "/metrics/worker"),
  };
  return cached;
}

/** Test seam: forget what was read, so a suite can pin its own values. */
export function resetAdminEnv(): void {
  cached = null;
}
